/**
 * useOfferContextNavigation — the "return to offer" / "exit offer context"
 * navigation handlers shared by `/map` and `/media-units`.
 *
 * Each route still creates its own route-typed `navigate` (via
 * `useNavigate({ from: Route.fullPath })`) and passes it in here — the hook
 * itself stays generic over `TFrom` so TS infers each caller's concrete
 * route type per call, the same way `stripAddToOfferId` (see
 * `../lib/strip-add-to-offer-id`) stays generic to avoid an unsafe cast.
 */
import * as React from 'react';
import type { UseNavigateResult } from '@tanstack/react-router';
import type { OfferContext } from './use-offer-context';
import { stripAddToOfferId } from '../lib/strip-add-to-offer-id';

/**
 * `navigate`'s type is a finite union of the two route-typed navigate
 * instances this hook is actually called with — `/map/` and `/media-units/`
 * — rather than a naked generic `TFrom`. TanStack Router's per-route search
 * schema resolution distributes over a union of literal route paths but
 * cannot resolve an opaque, unconstrained type parameter, so a generic
 * `<TFrom extends string>` here produces `search: never` the same way
 * `useNavigate()` without `from` does.
 */
type OfferContextNavigate = UseNavigateResult<'/map/'> | UseNavigateResult<'/media-units/'>;

export function useOfferContextNavigation(
  offerContext: OfferContext | null,
  navigate: OfferContextNavigate,
): { handleReturnToOffer: () => void; handleExitOfferContext: () => void } {
  const handleReturnToOffer = React.useCallback(() => {
    if (offerContext === null) return;
    void navigate({ to: '/offers/$offerId', params: { offerId: offerContext.offerId } });
  }, [navigate, offerContext]);

  const handleExitOfferContext = React.useCallback(() => {
    void navigate({ search: stripAddToOfferId });
  }, [navigate]);

  return { handleReturnToOffer, handleExitOfferContext };
}
